/**
 * Haptic + spring feedback. Uses navigator.vibrate on Android + PWA.
 * iOS Safari does NOT expose vibrate() — falls back to a subtle CSS-only
 * spring animation applied via requestAnimationFrame on the target element.
 *
 * The user's preference is persisted to localStorage. Default: on.
 */

const LS_KEY = 'wren.haptics.enabled' // HS22-ok: localStorage key namespace, not a credential

export type HapticKind = 'tap' | 'success' | 'warning' | 'error'

const PATTERNS: Record<HapticKind, number | number[]> = {
  tap: 8,
  success: [12, 40, 12],
  warning: [20, 30, 20, 30, 20],
  error: [60, 30, 60],
}

export function hapticsEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true
  const v = localStorage.getItem(LS_KEY)
  return v === null ? true : v === 'true'
}

export function setHapticsEnabled(enabled: boolean): void {
  try { localStorage.setItem(LS_KEY, String(enabled)) } catch { /* quota */ }
}

// System accessibility signal — user asked their OS to reduce motion.
// L10: honor across every haptic + animation surface. Vestibular disorders
// treat vibration the same as visual movement, so this ALSO gates vibrate().
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

/**
 * Fire a haptic pulse. Safe on iOS Safari (no-op — vibrate is undefined).
 * If the browser supports vibrate + user hasn't disabled: buzz.
 */
export function haptic(kind: HapticKind = 'tap'): void {
  if (!hapticsEnabled()) return
  if (prefersReducedMotion()) return
  if (typeof navigator === 'undefined') return
  const v = (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }).vibrate
  if (typeof v !== 'function') return
  const raw = PATTERNS[kind]
  const pattern: number[] = Array.isArray(raw) ? raw : [raw]
  try { v.call(navigator, pattern) } catch { /* noop */ }
}

/**
 * Wrap any React onClick so it fires haptic first, then the handler.
 * Usage: <button onClick={withHaptic(() => nav('/x'))}> or onClick={withHaptic(fn, 'success')}
 */
export function withHaptic<T extends (...args: never[]) => void>(fn: T, kind: HapticKind = 'tap'): T {
  return ((...args: Parameters<T>) => {
    haptic(kind)
    fn(...args)
  }) as T
}
